import React, { useMemo } from 'react';
import { Package, Receipt } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCube } from '@fortawesome/free-solid-svg-icons';
import { formatCurrency } from '../../../lib/utils';
import type { DealServiceFormData, Service } from '../../../lib/types';

interface PaymentMethodLite {
  id: string;
  name: string;
  payment_schedule?: { installments?: Array<Record<string, any>> } | null;
}

interface DealPaymentsRecapProps {
  language: string;
  serviceItems: DealServiceFormData[];
  services: Service[];
  paymentMethods: PaymentMethodLite[];
  /** Metodo dell'offerta: incassa tutte le righe prodotto. */
  productPaymentMethodId?: string | null;
  /** Apre l'editor della riga indicata (torna al tab Offerta). */
  onOpenLine?: (index: number) => void;
}

/**
 * Riepilogo di come verrà incassata l'offerta, riga per riga.
 *
 * Nasce da un vuoto: il metodo di pagamento di un servizio vive nel pannello
 * della sua riga, e le rate in un gruppo collassabile dentro quel pannello.
 * Su un'offerta con più righe, sapere "chi paga cosa e quando" richiedeva di
 * aprirle tutte una per una. Qui si legge in un colpo solo.
 *
 * È di sola lettura: ogni riga rimanda al proprio editor, che resta l'unico
 * posto dove si modifica. I prodotti stanno insieme perché condividono il
 * metodo dell'offerta ed escono come saldo unico alla firma.
 */
export function DealPaymentsRecap({
  language,
  serviceItems,
  services,
  paymentMethods,
  productPaymentMethodId,
  onOpenLine,
}: DealPaymentsRecapProps) {
  const it = language === 'it';

  const { productLines, serviceLines, total } = useMemo(() => {
    const products: Array<{ index: number; row: DealServiceFormData }> = [];
    const svcs: Array<{ index: number; row: DealServiceFormData }> = [];
    let sum = 0;

    serviceItems.forEach((row, index) => {
      const amount = Number((row as any).discounted_amount ?? row.amount ?? 0) || 0;
      sum += amount;
      if ((row as any).line_type === 'product') products.push({ index, row });
      else svcs.push({ index, row });
    });

    return { productLines: products, serviceLines: svcs, total: sum };
  }, [serviceItems]);

  if (serviceItems.length === 0) return null;

  const productMethod = paymentMethods.find(m => m.id === productPaymentMethodId);

  const amountOf = (row: DealServiceFormData) =>
    Number((row as any).discounted_amount ?? row.amount ?? 0) || 0;

  const nameOf = (row: DealServiceFormData) => {
    if ((row as any).line_type === 'product') {
      return row.custom_service_name || (it ? 'Articolo' : 'Product');
    }
    if (row.service_id?.toString().startsWith('custom-')) {
      return row.custom_service_name || (it ? 'Servizio personalizzato' : 'Custom service');
    }
    return services.find(s => s.id === row.service_id)?.title || (it ? 'Servizio' : 'Service');
  };

  const typeOf = (row: DealServiceFormData) => {
    if (row.service_id?.toString().startsWith('custom-')) return it ? 'Personalizzato' : 'Custom';
    const type = services.find(s => s.id === row.service_id)?.service_type;
    if (type === 'monthly') return it ? 'Mensile' : 'Monthly';
    if (type === 'annual') return it ? 'Annuale' : 'Annual';
    return it ? 'Una tantum' : 'One-off';
  };

  /** Metodo e piano rate di una riga servizio, con la stessa priorità della
   *  fatturazione: rate scritte sulla riga, altrimenti quelle del metodo. */
  const paymentOf = (row: DealServiceFormData) => {
    const ownInstallments = (row.payment_schedule as any)?.installments?.length ?? 0;
    const method = paymentMethods.find(m => m.id === row.payment_method_id);
    if (!method && ownInstallments > 0) {
      return {
        label: it ? 'Rate solo per questa offerta' : 'Deal-only schedule',
        rate: it ? `${ownInstallments} rate` : `${ownInstallments} installments`,
        missing: false,
      };
    }
    if (!method) {
      return {
        label: it ? 'Da scegliere' : 'To be chosen',
        rate: '—',
        missing: true,
      };
    }
    const count = ownInstallments || (method.payment_schedule?.installments?.length ?? 0);
    return {
      label: method.name,
      rate: count > 0
        ? (it ? `${count} ${count === 1 ? 'rata' : 'rate'}` : `${count} ${count === 1 ? 'installment' : 'installments'}`)
        : '—',
      missing: false,
    };
  };

  const rowCls = 'w-full flex items-center gap-3 px-5 py-2 text-left transition-colors hover:bg-black/[0.03] dark:hover:bg-white/[0.05]';

  return (
    <div className="glass-card rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-black/[0.06] dark:border-white/10">
        <Receipt className="h-4 w-4 text-black/70 dark:text-white/70" />
        <h3 className="text-[14px] font-semibold text-black dark:text-white">
          {it ? 'Riepilogo pagamenti' : 'Payments summary'}
        </h3>
      </div>

      {productLines.length > 0 && (
        <div className="border-b border-black/[0.06] dark:border-white/10">
          <div className="flex flex-wrap items-baseline gap-x-2 px-5 pt-3 pb-1">
            <span className="text-[10px] uppercase tracking-wide font-medium text-black/50 dark:text-white/50">
              {it ? `Prodotti (${productLines.length})` : `Products (${productLines.length})`}
            </span>
            <span className={`text-[12px] ${productMethod ? 'text-black/55 dark:text-white/55' : 'text-amber-600 dark:text-amber-400'}`}>
              {productMethod
                ? (it
                  ? `· ${productMethod.name}, saldo unico alla firma`
                  : `· ${productMethod.name}, settled in full on signature`)
                : (it ? '· metodo da scegliere' : '· method to be chosen')}
            </span>
          </div>
          {productLines.map(({ index, row }) => (
            <button
              key={`p-${index}`}
              type="button"
              onClick={() => onOpenLine?.(index)}
              className={rowCls}
            >
              <Package className="h-3.5 w-3.5 flex-shrink-0 text-black/40 dark:text-white/40" />
              <span className="flex-1 min-w-0 truncate text-[13px] text-black dark:text-white">
                {nameOf(row)}
              </span>
              {(row as any).quantity != null && (
                <span className="hidden sm:block text-[12px] text-black/45 dark:text-white/45 whitespace-nowrap">
                  {(row as any).quantity} {(row as any).unit_of_measure || ''}
                </span>
              )}
              <span className="text-[13px] font-medium tabular-nums text-black dark:text-white whitespace-nowrap">
                {formatCurrency(amountOf(row))}
              </span>
            </button>
          ))}
        </div>
      )}

      {serviceLines.length > 0 && (
        <div className="border-b border-black/[0.06] dark:border-white/10">
          <div className="px-5 pt-3 pb-1">
            <span className="text-[10px] uppercase tracking-wide font-medium text-black/50 dark:text-white/50">
              {it ? `Servizi (${serviceLines.length})` : `Services (${serviceLines.length})`}
            </span>
          </div>
          {serviceLines.map(({ index, row }) => {
            const payment = paymentOf(row);
            return (
              <button
                key={`s-${index}`}
                type="button"
                onClick={() => onOpenLine?.(index)}
                className={rowCls}
              >
                <FontAwesomeIcon icon={faCube} className="w-3 h-3 flex-shrink-0 text-black/40 dark:text-white/40" />
                <span className="flex-1 min-w-0 truncate text-[13px] text-black dark:text-white">
                  {nameOf(row)}
                  <span className="text-[12px] text-black/40 dark:text-white/40"> · {typeOf(row)}</span>
                </span>
                <span className={`hidden sm:block text-[12px] whitespace-nowrap ${payment.missing ? 'text-amber-600 dark:text-amber-400' : 'text-black/55 dark:text-white/55'}`}>
                  {payment.label}
                </span>
                <span className="hidden md:block text-[12px] text-black/40 dark:text-white/40 whitespace-nowrap w-[72px] text-right">
                  {payment.rate}
                </span>
                <span className="text-[13px] font-medium tabular-nums text-black dark:text-white whitespace-nowrap">
                  {formatCurrency(amountOf(row))}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-3 px-5 py-2.5 bg-black/[0.02] dark:bg-white/[0.03]">
        <span className="text-[11px] uppercase tracking-wide font-medium text-black/50 dark:text-white/50">
          {it ? 'Totale offerta' : 'Deal total'}
        </span>
        <span className="ml-auto text-[15px] font-semibold tabular-nums text-black dark:text-white">
          {formatCurrency(total)}
        </span>
      </div>
    </div>
  );
}
